// Native PDF generator — A4 landscape, vector everything. No
// html2canvas, no rasterisation. Charts are drawn as PDF primitives
// (lines, rects, polygons), text stays as text (searchable, copy-paste,
// crisp at any zoom), file size stays small.
//
// Layout target: looks like a printout of the live dashboard, not a
// document. 6 pages by default:
//   1. Cover                       — wordmark, headline KPIs, equity sparkline
//   2. KPI grid + Equity & Balance — 8 tiles + full-width chart
//   3. Drawdown + Risk-adjusted    — full-width chart + 9-up tile grid
//   4. Activity                    — Daily / Win-Loss / Hourly 3-up + Pos size + Bot vs Manual
//   5. Behavior                    — Calendar heatmap + Cumulative + Tag/Mistake tables
//   6+. Closed-positions journal   — paginated automatically
//
// jsPDF is dynamic-imported so it doesn't bloat the main bundle.

import {
  computeKpis, dailyAggregates, dailyBucketsFromPositions, drawdownSeries,
  cumulativeIntradayCurve, hourlyAggregates, pnlHistogram, recentMonths,
  riskMetrics, snapshotsToEquity, sourceBreakdown, volumeBuckets,
  type EquityPoint, type TradeRow,
} from '@/lib/metrics';
import { rStats, perTagStats, mistakeCorrelation } from '@/lib/r-multiples';
import { fmtMoney, fmtPct } from '@/lib/utils';
import {
  COLOR, drawCartesian, drawLineSeries, drawAreaSeries, drawBars,
  drawMonthGrid, drawTable, kpiTile, niceTicks, panel, sectionTitle,
  setFill, setStroke, setText,
  type Box, type Pdf, type RGB, type TableCol,
} from '@/lib/pdf-chart';
import type {
  AccountSnapshot, ClosedPosition, Tag,
} from '@/types/veritas';

// ── Page geometry (mm) ────────────────────────────────────────────────
const PAGE_W = 297;     // A4 landscape width
const PAGE_H = 210;     // A4 landscape height
const MARGIN = 10;
const HEADER_BAND = 9;
const FOOTER_BAND = 7;
const CONTENT_Y = HEADER_BAND;
const CONTENT_W = PAGE_W - 2 * MARGIN;

// ── Public API ────────────────────────────────────────────────────────
export type PdfMeta = {
  accountLogin: number;
  rangeStart: string;
  rangeEnd: string;
  ownerEmail: string | null;
  broker: string | null;
  eaVersion: string | null;
};

export type PdfData = {
  snapshots: AccountSnapshot[];
  trades: TradeRow[];
  positions: ClosedPosition[];
  positionTags: Map<number, Set<string>>;
  tags: Tag[];
};

export async function generateAccountPdf(meta: PdfMeta, data: PdfData): Promise<Blob> {
  const { jsPDF } = await import('jspdf');

  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  // jsPDF lets you fill the whole page with a colour by drawing a rect
  // from (0,0). We do this on every page so the dark theme is consistent.
  const ctx: PageContext = {
    pdf,
    meta,
    pageIndex: 1,
    totalPages: 0,  // filled in at end
  };

  // Pre-compute all derived values once so each page layout reads cleanly.
  const equity: EquityPoint[] = snapshotsToEquity(data.snapshots);
  const ddSeries = drawdownSeries(equity);
  const kpis = computeKpis(equity, data.trades);
  const daily = dailyAggregates(data.trades);
  const hourly = hourlyAggregates(data.trades);
  const histo = pnlHistogram(data.trades);
  const volBuckets = volumeBuckets(data.trades);
  const source = sourceBreakdown(data.trades);
  const rOverall = rStats(data.positions);
  const risk = riskMetrics(equity);
  const tagStats = perTagStats(data.positions, data.positionTags, data.tags).filter((s) => s.count > 0);
  const mistakeRows = mistakeCorrelation(
    data.positions,
    data.positionTags,
    data.tags.filter((t) => t.category === 'mistake'),
  ).filter((m) => m.taggedCount > 0);
  const dayBuckets = dailyBucketsFromPositions(data.positions);
  const months = recentMonths(3, new Date(meta.rangeEnd));
  const intraday = cumulativeIntradayCurve(data.positions);
  const symbols = [...new Set(data.trades.map((t) => t.symbol))];

  // ── Render pages ────────────────────────────────────────────────────
  paintPageBg(ctx);
  drawCover(ctx, { equity, kpis, risk, symbols, totalPositions: data.positions.length, totalTrades: data.trades.length });

  ctx.pdf.addPage();
  ctx.pageIndex++;
  paintPageBg(ctx);
  drawHeadline(ctx, { kpis, equity });

  ctx.pdf.addPage();
  ctx.pageIndex++;
  paintPageBg(ctx);
  drawDrawdownAndRisk(ctx, { ddSeries, risk, rOverall });

  ctx.pdf.addPage();
  ctx.pageIndex++;
  paintPageBg(ctx);
  drawActivity(ctx, { daily, hourly, histo, volBuckets, source, totalTrades: kpis.totalTrades });

  ctx.pdf.addPage();
  ctx.pageIndex++;
  paintPageBg(ctx);
  drawBehavior(ctx, { months, dayBuckets, intraday, tagStats, mistakeRows });

  drawJournalPages(ctx, data.positions);

  // Header / footer pass — we now know how many pages there are.
  ctx.totalPages = ctx.pdf.getNumberOfPages();
  for (let i = 1; i <= ctx.totalPages; i++) {
    ctx.pdf.setPage(i);
    ctx.pageIndex = i;
    drawHeaderFooter(ctx);
  }

  return ctx.pdf.output('blob');
}

export function pdfFilename(accountLogin: number, rangeStart: string, rangeEnd: string): string {
  return `veritas_report_${accountLogin}_${rangeStart}_to_${rangeEnd}.pdf`;
}

// ── Internal helpers ──────────────────────────────────────────────────
type PageContext = {
  pdf: Pdf;
  meta: PdfMeta;
  pageIndex: number;
  totalPages: number;
};

function paintPageBg(ctx: PageContext) {
  setFill(ctx.pdf, COLOR.bg);
  ctx.pdf.rect(0, 0, PAGE_W, PAGE_H, 'F');
}

function drawHeaderFooter(ctx: PageContext) {
  // Header band — wordmark + range
  setText(ctx.pdf, COLOR.textMid);
  ctx.pdf.setFont('helvetica', 'bold');
  ctx.pdf.setFontSize(7);
  ctx.pdf.text(`VALEXOS  ·  Veritas  ·  Account ${ctx.meta.accountLogin}`, MARGIN, 6);

  ctx.pdf.setFont('helvetica', 'normal');
  ctx.pdf.text(
    `${ctx.meta.rangeStart}  to  ${ctx.meta.rangeEnd}`,
    PAGE_W - MARGIN, 6, { align: 'right' },
  );

  // Thin line under header
  setStroke(ctx.pdf, COLOR.line);
  ctx.pdf.setLineWidth(0.1);
  ctx.pdf.line(MARGIN, 7.5, PAGE_W - MARGIN, 7.5);

  // Footer
  setText(ctx.pdf, COLOR.textDim);
  ctx.pdf.setFontSize(7);
  ctx.pdf.text('veritas.valexos.io', MARGIN, PAGE_H - 3);
  ctx.pdf.text(
    `Page ${ctx.pageIndex} of ${ctx.totalPages || '?'}`,
    PAGE_W - MARGIN, PAGE_H - 3, { align: 'right' },
  );
}

// ═════════════════════════════════════════════════════════════════════
// Page 1 — Cover
// ═════════════════════════════════════════════════════════════════════
function drawCover(
  ctx: PageContext,
  d: {
    equity: EquityPoint[];
    kpis: ReturnType<typeof computeKpis>;
    risk: ReturnType<typeof riskMetrics>;
    symbols: string[];
    totalPositions: number;
    totalTrades: number;
  },
) {
  const { pdf, meta } = ctx;

  // Wordmark — draw the Valexos triangle (▽) natively. Helvetica's
  // WinAnsi encoding doesn't include U+25BD, so passing it to pdf.text()
  // produced garbled output. jsPDF's triangle() primitive renders a
  // crisp filled triangle that matches the brand.
  setFill(pdf, COLOR.green);
  setStroke(pdf, COLOR.green);
  const tx = MARGIN + 1;
  const ty = MARGIN + 6;
  pdf.triangle(tx, ty, tx + 8, ty, tx + 4, ty + 6, 'F');

  setText(pdf, COLOR.text);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.text('VALEXOS', MARGIN + 13, MARGIN + 14);

  // LIVE PERFORMANCE pill (rounded rect with green text)
  pdf.setFontSize(7);
  setText(pdf, COLOR.green);
  panel(pdf, { x: MARGIN + 60, y: MARGIN + 8, w: 35, h: 6.5 },
        { fill: [0, 30, 18], stroke: COLOR.green, r: 3 });
  setText(pdf, COLOR.green);
  pdf.text('VERITAS REPORT', MARGIN + 60 + 17.5, MARGIN + 12.5, { align: 'center' });

  // Big title
  setText(pdf, COLOR.text);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(28);
  pdf.text('Account Performance Report', MARGIN, MARGIN + 30);

  setText(pdf, COLOR.textMid);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(13);
  const subtitle = meta.ownerEmail
    ? `Account ${meta.accountLogin}  ·  ${meta.ownerEmail}`
    : `Account ${meta.accountLogin}`;
  pdf.text(subtitle, MARGIN, MARGIN + 38);

  // Meta block (left side)
  const META_X = MARGIN;
  const META_Y = MARGIN + 47;
  const metaRows: [string, string][] = [
    ['Period',    `${meta.rangeStart} to ${meta.rangeEnd}`],
    ['Symbols',   d.symbols.slice(0, 6).join(', ') || '-'],
    ['Broker',    meta.broker ?? '-'],
    ['EA',        meta.eaVersion ?? '-'],
    ['Generated', new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC'],
    ['Activity',  `${d.totalPositions} closed positions, ${d.totalTrades} deal events`],
  ];
  pdf.setFontSize(8);
  for (let i = 0; i < metaRows.length; i++) {
    setText(pdf, COLOR.textMid);
    pdf.setFont('helvetica', 'bold');
    pdf.text(metaRows[i][0].toUpperCase(), META_X, META_Y + i * 5.5);
    setText(pdf, COLOR.text);
    pdf.setFont('helvetica', 'normal');
    pdf.text(metaRows[i][1], META_X + 22, META_Y + i * 5.5);
  }

  // 3 BIG headline KPI tiles (right side)
  const HEAD_X = MARGIN + 130;
  const HEAD_Y = MARGIN + 30;
  const HEAD_W = (CONTENT_W - 130) / 3 - 2;
  const HEAD_H = 28;

  drawBigKpi(pdf,
    { x: HEAD_X, y: HEAD_Y, w: HEAD_W, h: HEAD_H },
    'Net P&L',
    `${d.kpis.netPnL >= 0 ? '+' : ''}${fmtMoney(d.kpis.netPnL, '')}`,
    `${d.kpis.returnPct >= 0 ? '+' : ''}${fmtPct(d.kpis.returnPct)} return`,
    d.kpis.netPnL >= 0 ? COLOR.green : COLOR.red,
  );
  drawBigKpi(pdf,
    { x: HEAD_X + HEAD_W + 3, y: HEAD_Y, w: HEAD_W, h: HEAD_H },
    'Win rate',
    fmtPct(d.kpis.winRate),
    `${d.kpis.winners}W / ${d.kpis.losers}L  of  ${d.kpis.closedPositions} closed positions`,
  );
  drawBigKpi(pdf,
    { x: HEAD_X + (HEAD_W + 3) * 2, y: HEAD_Y, w: HEAD_W, h: HEAD_H },
    'Profit factor',
    Number.isFinite(d.kpis.profitFactor) ? d.kpis.profitFactor.toFixed(2) : 'inf',
    `Expectancy ${fmtMoney(d.kpis.expectancy, '')}`,
    COLOR.blue,
  );

  // Equity sparkline — full content width, fills the bottom half
  const SP_Y = MARGIN + 90;
  const SP_BOX: Box = { x: MARGIN, y: SP_Y, w: CONTENT_W, h: 60 };
  sectionTitle(pdf, MARGIN, SP_Y - 3, CONTENT_W, 'Equity & Balance — period overview',
    `${fmtMoney(d.kpis.initialBalance, '')} to ${fmtMoney(d.kpis.finalBalance, '')}`);
  drawEquityChart(pdf, SP_BOX, d.equity);

  // Risk preview strip (4 mini tiles at the bottom)
  const RISK_Y = MARGIN + 158;
  const RISK_W = (CONTENT_W) / 4 - 3;
  const RISK_H = 18;
  drawBigKpi(pdf, { x: MARGIN, y: RISK_Y, w: RISK_W, h: RISK_H },
    'Max drawdown', `-${fmtPct(d.kpis.maxDdPct)}`, fmtMoney(d.kpis.maxDdAbs, ''), COLOR.red);
  drawBigKpi(pdf, { x: MARGIN + (RISK_W + 4), y: RISK_Y, w: RISK_W, h: RISK_H },
    'Sharpe (ann.)', d.risk.sharpe == null ? '-' : d.risk.sharpe.toFixed(2), 'risk-adjusted');
  drawBigKpi(pdf, { x: MARGIN + (RISK_W + 4) * 2, y: RISK_Y, w: RISK_W, h: RISK_H },
    'Sortino (ann.)', d.risk.sortino == null ? '-' : d.risk.sortino.toFixed(2), 'downside-only');
  drawBigKpi(pdf, { x: MARGIN + (RISK_W + 4) * 3, y: RISK_Y, w: RISK_W, h: RISK_H },
    'Profitable days',
    d.risk.positiveDayPct == null ? '-' : `${d.risk.positiveDayPct.toFixed(1)}%`,
    `${d.risk.totalDays} trading days`);
}

// Bigger version of kpiTile for the cover and headline pages.
function drawBigKpi(
  pdf: Pdf, b: Box, label: string, value: string, sub?: string, valueColor: RGB = COLOR.text,
) {
  panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line, r: 2 });

  setText(pdf, COLOR.textMid);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(7);
  pdf.text(label.toUpperCase(), b.x + 4, b.y + 5.5);

  setText(pdf, valueColor);
  pdf.setFontSize(b.h > 22 ? 18 : 13);
  pdf.text(value, b.x + 4, b.y + (b.h > 22 ? 16 : 12));

  if (sub) {
    setText(pdf, COLOR.textDim);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.text(sub, b.x + 4, b.y + b.h - 3);
  }
}

// ═════════════════════════════════════════════════════════════════════
// Page 2 — Headline KPI grid + Equity & Balance chart
// ═════════════════════════════════════════════════════════════════════
function drawHeadline(
  ctx: PageContext,
  d: {
    kpis: ReturnType<typeof computeKpis>;
    equity: EquityPoint[];
  },
) {
  const { pdf } = ctx;
  sectionTitle(pdf, MARGIN, CONTENT_Y + 4, CONTENT_W, 'Headline KPIs');

  // 8 tiles, single row, full width
  const ROW_Y = CONTENT_Y + 8;
  const TILE_W = (CONTENT_W - 7 * 2) / 8;
  const TILE_H = 18;
  const tiles: { label: string; value: string; sub?: string; tone?: RGB; accent?: RGB }[] = [
    { label: 'Net P&L', value: `${d.kpis.netPnL >= 0 ? '+' : ''}${fmtMoney(d.kpis.netPnL, '')}`,
      sub: `${d.kpis.returnPct >= 0 ? '+' : ''}${fmtPct(d.kpis.returnPct)} return`,
      tone: d.kpis.netPnL >= 0 ? COLOR.green : COLOR.red,
      accent: d.kpis.netPnL >= 0 ? COLOR.green : COLOR.red },
    { label: 'Win rate', value: fmtPct(d.kpis.winRate), sub: `${d.kpis.winners}W / ${d.kpis.losers}L` },
    { label: 'Profit factor',
      value: Number.isFinite(d.kpis.profitFactor) ? d.kpis.profitFactor.toFixed(2) : 'inf',
      sub: `Expectancy ${fmtMoney(d.kpis.expectancy, '')}`, accent: COLOR.blue },
    { label: 'Total trades', value: String(d.kpis.totalTrades),
      sub: `${d.kpis.closedPositions} closed pos. · ${d.kpis.totalVolume.toFixed(2)} lots`,
      accent: COLOR.blue },
    { label: 'Max DD', value: `-${fmtPct(d.kpis.maxDdPct)}`,
      sub: fmtMoney(d.kpis.maxDdAbs, ''), tone: COLOR.red, accent: COLOR.red },
    { label: 'Avg win', value: fmtMoney(d.kpis.avgWin, ''), tone: COLOR.green, accent: COLOR.green },
    { label: 'Avg loss', value: fmtMoney(d.kpis.avgLoss, ''), tone: COLOR.red, accent: COLOR.red },
    { label: 'Streaks', value: `${d.kpis.longestWinStreak}W / ${d.kpis.longestLossStreak}L` },
  ];
  for (let i = 0; i < tiles.length; i++) {
    const t = tiles[i];
    kpiTile(pdf,
      { x: MARGIN + i * (TILE_W + 2), y: ROW_Y, w: TILE_W, h: TILE_H },
      { label: t.label, value: t.value, sub: t.sub, valueColor: t.tone, accent: t.accent });
  }

  // Equity chart — fills the rest of the page
  const CHART_Y = ROW_Y + TILE_H + 8;
  const CHART_H = PAGE_H - FOOTER_BAND - CHART_Y - 4;
  sectionTitle(pdf, MARGIN, CHART_Y - 3, CONTENT_W,
    'Equity & Balance', 'balance steps on close, equity tracks floating P&L');
  drawEquityChart(pdf, { x: MARGIN, y: CHART_Y, w: CONTENT_W, h: CHART_H }, d.equity);
}

// ═════════════════════════════════════════════════════════════════════
// Page 3 — Drawdown chart + 9-up risk-adjusted grid
// ═════════════════════════════════════════════════════════════════════
function drawDrawdownAndRisk(
  ctx: PageContext,
  d: {
    ddSeries: ReturnType<typeof drawdownSeries>;
    risk: ReturnType<typeof riskMetrics>;
    rOverall: ReturnType<typeof rStats>;
  },
) {
  const { pdf } = ctx;

  // Drawdown chart — full width, ~half page
  sectionTitle(pdf, MARGIN, CONTENT_Y + 4, CONTENT_W, 'Drawdown', 'distance from peak (%)');
  const DD_Y = CONTENT_Y + 8;
  const DD_H = 80;
  drawDrawdownChart(pdf, { x: MARGIN, y: DD_Y, w: CONTENT_W, h: DD_H }, d.ddSeries);

  // Risk-adjusted 9-up grid (3x3)
  sectionTitle(pdf, MARGIN, DD_Y + DD_H + 7, CONTENT_W,
    'Risk-adjusted performance',
    `${d.risk.totalDays} days  ·  ${d.risk.cumReturnPct >= 0 ? '+' : ''}${d.risk.cumReturnPct.toFixed(1)}% cum.`);
  const GRID_Y = DD_Y + DD_H + 11;
  const COLS = 3;
  const ROWS = 3;
  const TW = (CONTENT_W - (COLS - 1) * 4) / COLS;
  const TH = (PAGE_H - FOOTER_BAND - GRID_Y - 2) / ROWS - 3;

  const tiles: { label: string; value: string; sub?: string; accent?: RGB }[] = [
    { label: 'Sharpe (ann.)',     value: d.risk.sharpe         == null ? '-' : d.risk.sharpe.toFixed(2),         sub: 'risk-adjusted',
      accent: ratioAccent(d.risk.sharpe, 1) },
    { label: 'Sortino (ann.)',    value: d.risk.sortino        == null ? '-' : d.risk.sortino.toFixed(2),        sub: 'downside-only',
      accent: ratioAccent(d.risk.sortino, 1) },
    { label: 'Recovery factor',   value: d.risk.recoveryFactor == null ? '-' : d.risk.recoveryFactor.toFixed(2), sub: 'return / max DD',
      accent: ratioAccent(d.risk.recoveryFactor, 1) },
    { label: 'Calmar (ann.)',     value: d.risk.calmar         == null ? '-' : d.risk.calmar.toFixed(2),         sub: 'ann. ret / max DD',
      accent: ratioAccent(d.risk.calmar, 1) },
    { label: 'Max DD duration',   value: d.risk.maxDdDurationDays == null ? '-' : `${d.risk.maxDdDurationDays}d`,
      sub: d.risk.ongoingDdDays > 0 ? `${d.risk.ongoingDdDays}d below peak` : 'at or near peak' },
    { label: 'Profitable days',
      value: d.risk.positiveDayPct == null ? '-' : `${d.risk.positiveDayPct.toFixed(1)}%`,
      sub: `${d.risk.totalDays} trading days`,
      accent: pctAccent(d.risk.positiveDayPct) },
    { label: 'Expectancy R',
      value: d.rOverall.expectancyR == null ? '-' : `${d.rOverall.expectancyR > 0 ? '+' : ''}${d.rOverall.expectancyR.toFixed(2)}R`,
      sub: d.rOverall.withR === 0 ? 'no SL data' : `${d.rOverall.withR}/${d.rOverall.count} positions` },
    { label: 'Avg R',
      value: d.rOverall.avgR == null ? '-' : `${d.rOverall.avgR > 0 ? '+' : ''}${d.rOverall.avgR.toFixed(2)}R` },
    { label: 'Best / Worst R',
      value: d.rOverall.bestR == null ? '-'
        : `${d.rOverall.bestR > 0 ? '+' : ''}${d.rOverall.bestR.toFixed(1)} / ${(d.rOverall.worstR ?? 0).toFixed(1)}` },
  ];
  for (let i = 0; i < tiles.length; i++) {
    const r = Math.floor(i / COLS);
    const c = i % COLS;
    const t = tiles[i];
    kpiTile(pdf,
      { x: MARGIN + c * (TW + 4), y: GRID_Y + r * (TH + 3), w: TW, h: TH },
      { label: t.label, value: t.value, sub: t.sub, accent: t.accent });
  }
}

function ratioAccent(v: number | null, threshold: number): RGB | undefined {
  if (v == null) return COLOR.blue;
  if (v >= threshold * 2) return COLOR.green;
  if (v >= threshold) return COLOR.green;
  if (v >= 0) return COLOR.blue;
  return COLOR.red;
}
function pctAccent(v: number | null): RGB | undefined {
  if (v == null) return COLOR.blue;
  if (v >= 55) return COLOR.green;
  if (v >= 50) return COLOR.blue;
  if (v >= 45) return COLOR.amber;
  return COLOR.red;
}

// ═════════════════════════════════════════════════════════════════════
// Page 4 — Activity (Daily / WinLoss / Hourly + Pos size + Bot vs Manual)
// ═════════════════════════════════════════════════════════════════════
function drawActivity(
  ctx: PageContext,
  d: {
    daily: ReturnType<typeof dailyAggregates>;
    hourly: ReturnType<typeof hourlyAggregates>;
    histo: ReturnType<typeof pnlHistogram>;
    volBuckets: ReturnType<typeof volumeBuckets>;
    source: ReturnType<typeof sourceBreakdown>;
    totalTrades: number;
  },
) {
  const { pdf } = ctx;

  const TOP_Y = CONTENT_Y + 4;
  const TOP_H = 80;
  const COL_W = (CONTENT_W - 8) / 3;

  sectionTitle(pdf, MARGIN, TOP_Y, COL_W, 'Daily P&L');
  drawDailyChart(pdf, { x: MARGIN, y: TOP_Y + 4, w: COL_W, h: TOP_H }, d.daily);

  sectionTitle(pdf, MARGIN + COL_W + 4, TOP_Y, COL_W, 'Win / Loss distribution');
  drawHistoChart(pdf, { x: MARGIN + COL_W + 4, y: TOP_Y + 4, w: COL_W, h: TOP_H }, d.histo);

  sectionTitle(pdf, MARGIN + (COL_W + 4) * 2, TOP_Y, COL_W, 'Profit by hour (UTC)');
  drawHourlyChart(pdf, { x: MARGIN + (COL_W + 4) * 2, y: TOP_Y + 4, w: COL_W, h: TOP_H }, d.hourly);

  // Bottom row — Pos size dist (left, 3/5) + Bot vs Manual (right, 2/5)
  const BOT_Y = TOP_Y + TOP_H + 14;
  const POS_W = CONTENT_W * 0.6 - 4;
  const BVM_W = CONTENT_W * 0.4;

  sectionTitle(pdf, MARGIN, BOT_Y, POS_W, 'Position-size distribution');
  drawPosSizeBars(pdf, { x: MARGIN, y: BOT_Y + 4, w: POS_W, h: 50 }, d.volBuckets);

  sectionTitle(pdf, MARGIN + POS_W + 8, BOT_Y, BVM_W, 'Bot vs Manual');
  drawSourceCards(pdf, { x: MARGIN + POS_W + 8, y: BOT_Y + 4, w: BVM_W, h: 50 }, d.source, d.totalTrades);
}

// ═════════════════════════════════════════════════════════════════════
// Page 5 — Behavior (calendar + cumulative + tag/mistake tables)
// ═════════════════════════════════════════════════════════════════════
function drawBehavior(
  ctx: PageContext,
  d: {
    months: string[];
    dayBuckets: Map<string, { profit: number; trades: number; wins: number; losses: number }>;
    intraday: ReturnType<typeof cumulativeIntradayCurve>;
    tagStats: ReturnType<typeof perTagStats>;
    mistakeRows: ReturnType<typeof mistakeCorrelation>;
  },
) {
  const { pdf } = ctx;

  // Top: calendar heatmap (3 months, left 60%) + cumulative (right 40%)
  const TOP_Y = CONTENT_Y + 4;
  const TOP_H = 75;
  const CAL_W = CONTENT_W * 0.6 - 4;
  const CUM_W = CONTENT_W * 0.4;

  sectionTitle(pdf, MARGIN, TOP_Y, CAL_W, 'Calendar — last 3 months');
  // Compute maxAbs for tinting across all 3 months
  let maxAbs = 0;
  for (const ym of d.months) {
    for (const [k, b] of d.dayBuckets) {
      if (k.startsWith(ym)) maxAbs = Math.max(maxAbs, Math.abs(b.profit));
    }
  }
  const monthW = (CAL_W - 4) / 3;
  for (let i = 0; i < d.months.length; i++) {
    drawMonthGrid(pdf,
      { x: MARGIN + i * (monthW + 2), y: TOP_Y + 4, w: monthW, h: TOP_H },
      d.months[i], d.dayBuckets, maxAbs);
  }

  sectionTitle(pdf, MARGIN + CAL_W + 8, TOP_Y, CUM_W, 'Cumulative P&L through day (UTC)');
  drawCumulativeChart(pdf,
    { x: MARGIN + CAL_W + 8, y: TOP_Y + 4, w: CUM_W, h: TOP_H }, d.intraday);

  // Bottom: Tag performance + Mistake correlation tables
  const BOT_Y = TOP_Y + TOP_H + 14;
  const TBL_W = CONTENT_W / 2 - 4;

  if (d.tagStats.length > 0) {
    sectionTitle(pdf, MARGIN, BOT_Y, TBL_W, 'Tag performance');
    const tagCols: TableCol[] = [
      { label: 'Tag', width: TBL_W * 0.35, align: 'left' },
      { label: 'N', width: TBL_W * 0.10, align: 'right' },
      { label: 'Win %', width: TBL_W * 0.15, align: 'right' },
      { label: 'P&L', width: TBL_W * 0.20, align: 'right' },
      { label: 'Exp R', width: TBL_W * 0.20, align: 'right' },
    ];
    const tagRowsData = d.tagStats.slice(0, 12).map((t) => [
      t.tagName,
      String(t.count),
      `${t.winRate.toFixed(0)}%`,
      { text: fmtMoney(t.totalPnL, ''), color: t.totalPnL >= 0 ? COLOR.green : COLOR.red },
      t.expectancyR == null ? '-' : `${t.expectancyR > 0 ? '+' : ''}${t.expectancyR.toFixed(2)}R`,
    ]);
    drawTable(pdf, MARGIN, BOT_Y + 4, tagCols, tagRowsData);
  }

  if (d.mistakeRows.length > 0) {
    sectionTitle(pdf, MARGIN + TBL_W + 8, BOT_Y, TBL_W, 'Mistake correlation');
    const mCols: TableCol[] = [
      { label: 'Mistake', width: TBL_W * 0.30, align: 'left' },
      { label: 'Tagged', width: TBL_W * 0.15, align: 'right' },
      { label: 'Tag Avg', width: TBL_W * 0.18, align: 'right' },
      { label: 'Untag Avg', width: TBL_W * 0.18, align: 'right' },
      { label: 'Delta', width: TBL_W * 0.19, align: 'right' },
    ];
    const mRowsData = d.mistakeRows.slice(0, 12).map((m) => [
      m.tagName,
      String(m.taggedCount),
      { text: fmtMoney(m.taggedAvgPnL, ''), color: m.taggedAvgPnL >= 0 ? COLOR.green : COLOR.red },
      { text: fmtMoney(m.untaggedAvgPnL, ''), color: m.untaggedAvgPnL >= 0 ? COLOR.green : COLOR.red },
      { text: fmtMoney(m.pnlDelta, ''), color: m.pnlDelta <= 0 ? COLOR.green : COLOR.red },
    ]);
    drawTable(pdf, MARGIN + TBL_W + 8, BOT_Y + 4, mCols, mRowsData);
  }
}

// ═════════════════════════════════════════════════════════════════════
// Pages 6+ — Closed-positions journal
// ═════════════════════════════════════════════════════════════════════
function drawJournalPages(ctx: PageContext, positions: ClosedPosition[]) {
  const { pdf } = ctx;
  if (positions.length === 0) return;

  pdf.addPage();
  ctx.pageIndex++;
  paintPageBg(ctx);

  sectionTitle(pdf, MARGIN, CONTENT_Y + 4, CONTENT_W,
    `Closed positions (${positions.length})`,
    'newest first  ·  bot trades shown lower-case in Source column');

  const sorted = positions.slice().sort((a, b) =>
    (b.closed_at ?? '').localeCompare(a.closed_at ?? ''));

  // Wider table in landscape — more useful columns fit cleanly
  const cols: TableCol[] = [
    { label: 'Closed (UTC)',    width: 32, align: 'left' },
    { label: 'Symbol',          width: 18, align: 'left' },
    { label: 'Side',            width: 13, align: 'left' },
    { label: 'Vol',             width: 14, align: 'right' },
    { label: 'Entry',           width: 22, align: 'right' },
    { label: 'Exit',            width: 22, align: 'right' },
    { label: 'Stop',            width: 22, align: 'right' },
    { label: 'P&L',             width: 24, align: 'right' },
    { label: 'R',               width: 18, align: 'right' },
    { label: 'Magic',           width: 22, align: 'right' },
    { label: 'Source',          width: 16, align: 'right' },
  ];

  const rows = sorted.map((p) => {
    const r = computeR(p);
    const isBuy = p.side_code === 0;
    const isProfit = p.realized_profit >= 0;
    return [
      p.closed_at?.replace('T', ' ').slice(0, 16) ?? '-',
      p.symbol,
      { text: isBuy ? 'BUY' : 'SELL', color: isBuy ? COLOR.green : COLOR.red, bold: true },
      p.opened_volume.toFixed(2),
      p.entry_price?.toFixed(5) ?? '-',
      p.exit_price?.toFixed(5) ?? '-',
      p.initial_sl?.toFixed(5) ?? '-',
      { text: `${isProfit ? '+' : ''}${p.realized_profit.toFixed(2)}`,
        color: isProfit ? COLOR.green : COLOR.red, bold: true },
      r == null ? '-' : `${r > 0 ? '+' : ''}${r.toFixed(2)}`,
      String(p.magic ?? 0),
      p.magic && p.magic !== 0 ? 'bot' : 'manual',
    ];
  });

  drawTable(pdf, MARGIN, CONTENT_Y + 8, cols, rows, {
    rowHeight: 4.4,
    bodySize: 7,
    pageLimit: PAGE_H - FOOTER_BAND - 2,
    onPageBreak: () => {
      pdf.addPage();
      ctx.pageIndex++;
      paintPageBg(ctx);
      sectionTitle(pdf, MARGIN, CONTENT_Y + 4, CONTENT_W,
        `Closed positions (continued)`);
      return CONTENT_Y + 8;
    },
  });
}

function computeR(p: ClosedPosition): number | null {
  if (p.entry_price == null || p.exit_price == null
      || p.initial_sl == null || p.initial_sl === 0) return null;
  const isBuy = p.side_code === 0;
  const risk = isBuy ? p.entry_price - p.initial_sl : p.initial_sl - p.entry_price;
  if (!Number.isFinite(risk) || risk <= 0) return null;
  const profit = isBuy ? p.exit_price - p.entry_price : p.entry_price - p.exit_price;
  const r = profit / risk;
  return Number.isFinite(r) ? r : null;
}

// ═════════════════════════════════════════════════════════════════════
// Chart-specific helpers (use the primitives)
// ═════════════════════════════════════════════════════════════════════
function drawEquityChart(pdf: Pdf, b: Box, equity: EquityPoint[]) {
  if (equity.length === 0) {
    panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line });
    return;
  }
  // Use indices 0..N-1 for x to give each snapshot equal pixel width.
  const balPoints = equity.map((p, i) => ({ x: i, y: p.balance }));
  const eqPoints  = equity.filter((p) => p.equity != null).map((p, i) => ({ x: i, y: p.equity as number }));

  const allYs = balPoints.map((p) => p.y).concat(eqPoints.map((p) => p.y));
  const yMin = Math.min(...allYs);
  const yMax = Math.max(...allYs);
  const yPad = (yMax - yMin) * 0.05 || Math.abs(yMax) * 0.01 || 1;
  const yTicks = niceTicks(yMin - yPad, yMax + yPad, 5)
    .map((v) => ({ v, label: v.toLocaleString('en-US', { maximumFractionDigits: 0 }) }));

  // Pick ~6 X-axis date labels
  const xTicks = pickDateTicks(equity, 6);

  const { plotBox, xToMm, yToMm } = drawCartesian(pdf, b, {
    xMin: 0, xMax: Math.max(1, equity.length - 1),
    yMin: yMin - yPad, yMax: yMax + yPad,
    yTicks, xTicks,
    axisLeft: 18, axisBottom: 6,
  });

  // Balance line — solid green
  drawLineSeries(pdf, plotBox, balPoints, xToMm, yToMm, { color: COLOR.green, width: 0.5 });
  // Equity line — dashed blue (overlay)
  if (eqPoints.length > 1) {
    drawLineSeries(pdf, plotBox, eqPoints, xToMm, yToMm, { color: COLOR.blue, width: 0.4, dashed: true });
  }

  // Legend (bottom-right corner of plot box)
  setFill(pdf, COLOR.green);
  pdf.rect(plotBox.x + 1, plotBox.y + 1, 2, 0.6, 'F');
  setText(pdf, COLOR.textMid);
  pdf.setFontSize(6);
  pdf.text('Balance', plotBox.x + 4, plotBox.y + 1.7);
  setFill(pdf, COLOR.blue);
  pdf.rect(plotBox.x + 16, plotBox.y + 1, 2, 0.6, 'F');
  pdf.text('Equity', plotBox.x + 19, plotBox.y + 1.7);
}

function drawDrawdownChart(pdf: Pdf, b: Box, dd: { t: string; ddPct: number }[]) {
  if (dd.length === 0) {
    panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line });
    return;
  }
  const points = dd.map((p, i) => ({ x: i, y: p.ddPct }));
  const yMin = Math.min(0, ...points.map((p) => p.y));
  const yMax = 0;
  const yPad = Math.abs(yMin) * 0.05 || 0.5;

  const yTicks = niceTicks(yMin - yPad, yMax, 5)
    .map((v) => ({ v, label: `${v.toFixed(1)}%` }));
  const xTicks = pickDateTicks(dd, 6);

  const { plotBox, xToMm, yToMm } = drawCartesian(pdf, b, {
    xMin: 0, xMax: Math.max(1, dd.length - 1),
    yMin: yMin - yPad, yMax,
    yTicks, xTicks,
    axisLeft: 14, axisBottom: 6,
  });

  drawAreaSeries(pdf, plotBox, points, 0, xToMm, yToMm,
    { fill: COLOR.red, stroke: COLOR.red, opacity: 0.35 });
}

function drawDailyChart(pdf: Pdf, b: Box, daily: ReturnType<typeof dailyAggregates>) {
  if (daily.length === 0) {
    panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line });
    return;
  }
  const ys = daily.map((d) => d.profit);
  const yMin = Math.min(0, ...ys);
  const yMax = Math.max(0, ...ys);
  const yPad = (yMax - yMin) * 0.1 || 1;

  const yTicks = niceTicks(yMin - yPad / 4, yMax + yPad / 4, 4)
    .map((v) => ({ v, label: v.toFixed(0) }));
  const N = daily.length;

  // Show every Nth date label so they don't overlap
  const step = Math.max(1, Math.ceil(N / 6));
  const xTicks = daily.map((d, i) => ({ v: i, label: d.date.slice(5) }))
                       .filter((_, i) => i % step === 0);

  const { plotBox, xToMm, yToMm } = drawCartesian(pdf, b, {
    xMin: -0.5, xMax: N - 0.5,
    yMin: yMin - yPad / 4, yMax: yMax + yPad / 4,
    yTicks, xTicks, axisLeft: 12,
  });

  // Baseline
  setStroke(pdf, COLOR.line);
  pdf.setLineWidth(0.15);
  pdf.line(plotBox.x, yToMm(0), plotBox.x + plotBox.w, yToMm(0));

  drawBars(pdf, plotBox,
    daily.map((d, i) => ({
      x: i, v: d.profit,
      color: d.profit >= 0 ? COLOR.green : COLOR.red,
    })),
    xToMm, yToMm, { baseline: 0 });
}

function drawHistoChart(pdf: Pdf, b: Box, histo: ReturnType<typeof pnlHistogram>) {
  if (histo.length === 0) {
    panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line });
    return;
  }
  const ys = histo.map((h) => h.count);
  const yMax = Math.max(...ys, 1);
  const yTicks = niceTicks(0, yMax, 4).map((v) => ({ v, label: v.toFixed(0) }));

  const xs = histo.map((h) => h.midpoint);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const xPad = (xMax - xMin) * 0.05 || 1;

  const xTicks = pickEvenTicks(xMin, xMax, 5).map((v) => ({ v, label: v.toFixed(0) }));

  const { plotBox, xToMm, yToMm } = drawCartesian(pdf, b, {
    xMin: xMin - xPad, xMax: xMax + xPad,
    yMin: 0, yMax,
    yTicks, xTicks, axisLeft: 10,
  });

  drawBars(pdf, plotBox,
    histo.map((h) => ({
      x: h.midpoint, v: h.count,
      color: h.positive ? COLOR.green : COLOR.red,
    })),
    xToMm, yToMm, { baseline: 0 });
}

function drawHourlyChart(pdf: Pdf, b: Box, hourly: ReturnType<typeof hourlyAggregates>) {
  if (hourly.length === 0) {
    panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line });
    return;
  }
  // Fill in missing hours so the X-axis is always 0..23
  const fill = new Map(hourly.map((h) => [h.hour, h]));
  const data = Array.from({ length: 24 }, (_, h) => fill.get(h) ?? { hour: h, profit: 0, trades: 0 });

  const ys = data.map((d) => d.profit);
  const yMin = Math.min(0, ...ys);
  const yMax = Math.max(0, ...ys);
  const yPad = (yMax - yMin) * 0.1 || 1;

  const yTicks = niceTicks(yMin - yPad / 4, yMax + yPad / 4, 4)
    .map((v) => ({ v, label: v.toFixed(0) }));
  const xTicks = [0, 4, 8, 12, 16, 20].map((h) => ({ v: h, label: `${h}h` }));

  const { plotBox, xToMm, yToMm } = drawCartesian(pdf, b, {
    xMin: -0.5, xMax: 23.5,
    yMin: yMin - yPad / 4, yMax: yMax + yPad / 4,
    yTicks, xTicks, axisLeft: 12,
  });

  setStroke(pdf, COLOR.line);
  pdf.setLineWidth(0.15);
  pdf.line(plotBox.x, yToMm(0), plotBox.x + plotBox.w, yToMm(0));

  drawBars(pdf, plotBox,
    data.map((d) => ({
      x: d.hour, v: d.profit,
      color: d.profit >= 0 ? COLOR.blue : COLOR.red,
    })),
    xToMm, yToMm, { baseline: 0 });
}

function drawCumulativeChart(pdf: Pdf, b: Box, intraday: ReturnType<typeof cumulativeIntradayCurve>) {
  if (intraday.length === 0 || intraday.every((p) => p.days === 0)) {
    panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line });
    return;
  }
  const points = intraday.map((p) => ({ x: p.hour, y: p.avgCumulative }));
  const ys = points.map((p) => p.y);
  const yMin = Math.min(0, ...ys);
  const yMax = Math.max(0, ...ys);
  const yPad = (yMax - yMin) * 0.1 || 1;

  const yTicks = niceTicks(yMin - yPad, yMax + yPad, 4).map((v) => ({ v, label: v.toFixed(0) }));
  const xTicks = [0, 4, 8, 12, 16, 20, 23].map((h) => ({ v: h, label: `${h}h` }));

  const { plotBox, xToMm, yToMm } = drawCartesian(pdf, b, {
    xMin: 0, xMax: 23,
    yMin: yMin - yPad, yMax: yMax + yPad,
    yTicks, xTicks, axisLeft: 12,
  });

  // Baseline
  setStroke(pdf, COLOR.line);
  pdf.setLineWidth(0.15);
  pdf.line(plotBox.x, yToMm(0), plotBox.x + plotBox.w, yToMm(0));

  drawAreaSeries(pdf, plotBox, points, 0, xToMm, yToMm,
    { fill: COLOR.blue, stroke: COLOR.blue, opacity: 0.25 });
}

function drawPosSizeBars(pdf: Pdf, b: Box, buckets: ReturnType<typeof volumeBuckets>) {
  panel(pdf, b, { fill: COLOR.panel, stroke: COLOR.line });
  const padX = 4;
  const padY = 4;
  const innerW = b.w - 2 * padX;
  const rowH = (b.h - 2 * padY) / Math.max(buckets.length, 1);

  for (let i = 0; i < buckets.length; i++) {
    const bk = buckets[i];
    const y = b.y + padY + i * rowH;
    // Label
    setText(pdf, COLOR.text);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.text(bk.label, b.x + padX, y + rowH / 2 + 1);
    // Right-side: count / pnl
    setText(pdf, COLOR.text);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.text(`${bk.count}`, b.x + b.w - padX - 28, y + rowH / 2 + 1, { align: 'right' });
    setText(pdf, bk.profit >= 0 ? COLOR.green : COLOR.red);
    pdf.text(fmtMoney(bk.profit, ''), b.x + b.w - padX, y + rowH / 2 + 1, { align: 'right' });
    // Bar (proportional to share)
    const barX = b.x + padX + 22;
    const barMax = innerW - 22 - 30;  // leave space for count + p&l
    const barW = Math.max(0.5, bk.countShare * barMax);
    setFill(pdf, bk.profit >= 0 ? COLOR.green : COLOR.red);
    pdf.rect(barX, y + rowH / 2 - 1.5, barW, 3, 'F');
    // Track behind bar
    setStroke(pdf, COLOR.lineSoft);
    pdf.setLineWidth(0.1);
    pdf.line(barX, y + rowH / 2, barX + barMax, y + rowH / 2);
  }
}

function drawSourceCards(
  pdf: Pdf, b: Box,
  source: ReturnType<typeof sourceBreakdown>,
  totalTrades: number,
) {
  const cardW = (b.w - 4) / 2;

  for (let i = 0; i < 2; i++) {
    const isBot = i === 0;
    const s = isBot ? source.bot : source.manual;
    const accent = isBot ? COLOR.blue : COLOR.green;
    const cb: Box = { x: b.x + i * (cardW + 4), y: b.y, w: cardW, h: b.h };
    panel(pdf, cb, { fill: COLOR.panel, stroke: COLOR.line });

    setText(pdf, accent);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7);
    pdf.text(isBot ? 'BOT' : 'MANUAL', cb.x + 4, cb.y + 5);

    const share = totalTrades > 0 ? (s.count / totalTrades) * 100 : 0;
    setText(pdf, COLOR.textDim);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7);
    pdf.text(`${share.toFixed(0)}%`, cb.x + cb.w - 4, cb.y + 5, { align: 'right' });

    // Stats grid 2x2
    const labels: [string, string, RGB?][] = [
      ['Trades', String(s.count)],
      ['Win rate', `${s.winRate.toFixed(0)}%`],
      ['P&L', fmtMoney(s.profit, ''), s.profit >= 0 ? COLOR.green : COLOR.red],
      ['Volume', `${s.volume.toFixed(2)} lots`],
    ];
    const cellW = (cb.w - 8) / 2;
    const cellH = (cb.h - 12) / 2;
    for (let j = 0; j < labels.length; j++) {
      const cx = cb.x + 4 + (j % 2) * cellW;
      const cy = cb.y + 10 + Math.floor(j / 2) * cellH;
      setText(pdf, COLOR.textDim);
      pdf.setFontSize(6);
      pdf.text(labels[j][0], cx, cy);
      setText(pdf, labels[j][2] ?? COLOR.text);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.text(labels[j][1], cx, cy + 5);
      pdf.setFont('helvetica', 'normal');
    }
  }
}

// ── Tick utilities ────────────────────────────────────────────────────
function pickDateTicks<T extends { t: string }>(
  series: T[], target = 6,
): { v: number; label: string }[] {
  if (series.length === 0) return [];
  const step = Math.max(1, Math.ceil(series.length / target));
  return series
    .map((p, i) => ({ v: i, label: p.t.slice(5, 10) }))
    .filter((_, i) => i % step === 0);
}

function pickEvenTicks(min: number, max: number, count: number): number[] {
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + i * step);
}
